import React, { useEffect, useRef, useState } from "react";

/* ==========================================================================
   TagScanner — live camera QR scanning
   --------------------------------------------------------------------------
   Two decoders, picked at runtime:

   1. Native BarcodeDetector — zero dependencies, hardware accelerated.
      Chrome / Edge on Android, ChromeOS, and desktop. Covers most warehouse
      Android handhelds.
   2. html5-qrcode — lazily imported ONLY if the native API is missing, so
      Android users never download it. Needed for iOS Safari and Firefox,
      which have no BarcodeDetector.

      npm i html5-qrcode

   Camera access requires a secure context. localhost counts; a LAN IP on
   your phone does not. See the notes at the bottom of this file.
   ========================================================================== */

const DEDUPE_MS = 2500; // same code won't re-fire inside this window

/* Which symbologies to decode.

   Asset tags printed by this app are QR, so "tag" is the default and nothing
   about the existing scan flow changes. Receiving new equipment is the other
   case: manufacturers' boxes carry the serial as a 1D barcode — Code 128 on
   Lenovo and Apple cartons, Code 39 on older stock, occasionally Data Matrix —
   so that mode asks for those as well.

   Every format costs decode time on every frame, which is why this is a choice
   rather than "all of them": a handset trying eight symbologies at 10fps gets
   noticeably worse at the one you actually want.

   The two decoders name formats differently, so each set is expressed once and
   mapped per engine below. */
export const SCAN_FORMATS = {
  tag: {
    native: ["qr_code"],
    html5: ["QR_CODE"],
  },
  box: {
    native: ["code_128", "code_39", "data_matrix", "qr_code", "itf"],
    html5: ["CODE_128", "CODE_39", "DATA_MATRIX", "QR_CODE", "ITF"],
  },
};

/* Set to true to skip the native BarcodeDetector entirely and always use
   html5-qrcode. Worth trying if the camera is live but nothing decodes:
   several platforms expose BarcodeDetector without a working backend, and
   desktop Linux Chrome is the usual offender. */
const FORCE_FALLBACK = false;

export default function TagScanner({ onScan, onClose, mode = "tag" }) {
  const formatSet = SCAN_FORMATS[mode] ?? SCAN_FORMATS.tag;
  const hostRef = useRef(null);
  const cleanupRef = useRef(null);
  const lastRef = useRef({ value: null, at: 0 });
  const liveRef = useRef(true);

  const [status, setStatus] = useState("starting"); // starting | scanning | error
  const [problem, setProblem] = useState(null);
  const [engine, setEngine] = useState(null);
  const [hit, setHit] = useState(null);

  // Liveness diagnostics. Without these, a decoder that is running but never
  // matching is indistinguishable from one that never started.
  const framesRef = useRef(0);
  const decodeErrRef = useRef(null);
  const [debug, setDebug] = useState({ frames: 0, err: null });

  useEffect(() => {
    const id = setInterval(
      () => setDebug({ frames: framesRef.current, err: decodeErrRef.current }),
      1000
    );
    return () => clearInterval(id);
  }, []);

  // Fire once per code per window. Scanners emit the same value ~10x/second.
  const emit = (raw) => {
    const now = Date.now();
    if (lastRef.current.value === raw && now - lastRef.current.at < DEDUPE_MS) return;
    lastRef.current = { value: raw, at: now };

    // Physical feedback matters when someone is holding a device at arm's
    // length and can't watch the screen.
    if (navigator.vibrate) navigator.vibrate(60);
    setHit(raw);
    setTimeout(() => liveRef.current && setHit(null), 900);

    onScan(raw);
  };

  useEffect(() => {
    liveRef.current = true;

    const fail = (code, detail) => {
      if (!liveRef.current) return;
      setProblem({ code, detail });
      setStatus("error");
    };

    const startNative = async () => {
      const supported = await window.BarcodeDetector.getSupportedFormats();
      /* Ask only for what this platform actually implements. Passing an
         unsupported format to the constructor throws, which would drop an
         Android handset to the 300KB fallback over a symbology it was never
         going to need. */
      const wanted = formatSet.native.filter((f) => supported.includes(f));
      if (!wanted.length) return false;

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } },
      });

      const video = document.createElement("video");
      video.setAttribute("playsinline", "true"); // iOS refuses fullscreen-less playback without this
      video.muted = true;
      video.className = "ts-video";
      video.srcObject = stream;
      hostRef.current.appendChild(video);
      await video.play();

      const detector = new window.BarcodeDetector({ formats: wanted });
      let frame = 0;
      let scanning = true;

      const tick = async () => {
        if (!scanning) return;
        // Decode every 3rd frame — 60fps decoding drains battery for no gain.
        if (frame++ % 3 === 0 && video.readyState >= 2) {
          framesRef.current++;
          try {
            const found = await detector.detect(video);
            if (found.length) emit(found[0].rawValue);
          } catch (e) {
            // A persistent error here means the platform advertises the API
            // but has no working backend. Record it so the UI can say so.
            decodeErrRef.current = e?.message || String(e);
          }
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);

      cleanupRef.current = () => {
        scanning = false;
        stream.getTracks().forEach((t) => t.stop());
        video.srcObject = null;
        video.remove();
      };

      setEngine("Native BarcodeDetector");
      setStatus("scanning");
      return true;
    };

    const startFallback = async () => {
      const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import("html5-qrcode");

      const id = "ts-region";
      const region = document.createElement("div");
      region.id = id;
      region.className = "ts-region";
      hostRef.current.appendChild(region);

      const scanner = new Html5Qrcode(id, {
        formatsToSupport: formatSet.html5
          .map((name) => Html5QrcodeSupportedFormats[name])
          .filter((v) => v !== undefined),
        verbose: false,
      });

      await scanner.start(
        // First argument must have exactly one key — facingMode or deviceId.
        // Resolution goes in videoConstraints below, not here.
        { facingMode: "environment" },
        {
          fps: 10,
          // Default capture is often 640x480, which leaves QR modules only a
          // pixel or two wide at any real distance. Ask for more.
          videoConstraints: {
            facingMode: "environment",
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          // No qrbox. Any crop region has to be mapped from the displayed
          // element back onto the source frame, and any CSS that resizes the
          // video breaks that mapping silently. Scanning the whole frame has
          // no mapping to get wrong.
        },
        (text) => emit(text),
        () => {
          // Fires every frame a code is NOT found. Useful as a heartbeat.
          framesRef.current++;
        }
      );

      cleanupRef.current = async () => {
        try {
          await scanner.stop();
          await scanner.clear();
        } catch {
          /* already stopped */
        }
        region.remove();
      };

      setEngine("html5-qrcode");
      setStatus("scanning");
    };

    (async () => {
      // The most common failure, and the one with the least helpful native error.
      if (!navigator.mediaDevices?.getUserMedia) {
        fail("insecure", window.location.protocol);
        return;
      }

      try {
        if (!FORCE_FALLBACK && "BarcodeDetector" in window) {
          const ok = await startNative();
          if (ok) return;
        }
        await startFallback();
      } catch (err) {
        const name = err?.name || "";
        if (name === "NotAllowedError" || name === "SecurityError") fail("denied");
        else if (name === "NotFoundError" || name === "DevicesNotFoundError") fail("nocamera");
        else if (name === "NotReadableError") fail("busy");
        else fail("unknown", err?.message || String(err));
      }
    })();

    return () => {
      liveRef.current = false;
      const fn = cleanupRef.current;
      cleanupRef.current = null;
      if (fn) Promise.resolve(fn()).catch(() => {});
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="ts">
      <style>{CSS}</style>

      <header className="ts-head">
        <div>
          <div className="ts-eyebrow">Camera</div>
          <p className="ts-status">
            {status === "starting" && "Requesting camera…"}
            {status === "scanning" && "Point at a property tag"}
            {status === "error" && "Camera unavailable"}
          </p>
        </div>
        {onClose && (
          <button className="ts-close" onClick={onClose} aria-label="Stop scanning">
            ✕
          </button>
        )}
      </header>

      {status !== "error" && (
        <div className={"ts-stage" + (hit ? " is-hit" : "")}>
          <div className="ts-host" ref={hostRef} />
          {hit && <div className="ts-hit">{hit.replace(/^.*\/a\//, "")}</div>}
        </div>
      )}

      {status === "error" && <Problem problem={problem} />}

      {status === "scanning" && (
        <div className="ts-engine">
          <p>
            Decoder: <strong>{engine}</strong> · {debug.frames} decode attempts
          </p>
          {debug.frames === 0 && (
            <p className="ts-warn">
              Video is live but the decode loop has not run. The camera stream started without a
              usable decoder.
            </p>
          )}
          {debug.err && (
            <p className="ts-warn">
              Decoder error: {debug.err} — this platform advertises BarcodeDetector without a
              working backend. Set FORCE_FALLBACK to true at the top of TagScanner.jsx.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Problem({ problem }) {
  const { code, detail } = problem || {};

  const copy = {
    insecure: {
      title: "Camera needs a secure connection",
      body: `This page is on ${detail || "http"}. Browsers only expose the camera over HTTPS or on localhost, and give no prompt otherwise. Start an HTTPS tunnel and open that URL instead — see the notes in TagScanner.jsx.`,
    },
    denied: {
      title: "Camera permission was refused",
      body: "Tap the padlock or camera icon in the address bar, allow camera for this site, then reload. On iOS check Settings › Safari › Camera.",
    },
    nocamera: {
      title: "No camera found",
      body: "This device reports no video input. Manual tag entry still works.",
    },
    busy: {
      title: "Camera is in use",
      body: "Another tab or app holds the camera. Close it and try again.",
    },
    unknown: {
      title: "Could not start the camera",
      body: detail || "Unexpected error.",
    },
  }[code] || { title: "Could not start the camera", body: "" };

  return (
    <div className="ts-problem">
      <p className="ts-problem-title">{copy.title}</p>
      <p className="ts-problem-body">{copy.body}</p>
    </div>
  );
}

const CSS = `
.ts { --ts-ink: #16202B; --ts-zinc: #6D7C8A; --ts-rule: #D5DBE1; --ts-signal: #0E7C86; --ts-red: #9C2A1E;
  font-family: 'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif; color: var(--ts-ink); }
.ts * { box-sizing: border-box; }
.ts-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.ts-eyebrow { font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 10px; font-weight: 600;
  letter-spacing: 0.14em; text-transform: uppercase; color: var(--ts-zinc); }
.ts-status { font-size: 15px; font-weight: 600; margin: 3px 0 0; }
.ts-close { border: 0; background: none; color: var(--ts-zinc); font-size: 15px; cursor: pointer; padding: 4px 6px; }
.ts-close:hover { color: var(--ts-ink); }

.ts-stage { position: relative; width: 100%; max-width: 420px; overflow: hidden;
  border-radius: 3px; background: #0B1219; }

/* Do NOT force object-fit or !important dimensions on the library's video.
   html5-qrcode maps decode coordinates from the element back onto the source
   frame; any CSS that crops or scales it breaks that mapping without error. */
.ts-region { width: 100%; }
.ts-region video { width: 100% !important; height: auto !important; display: block; }
.ts-host { width: 100%; }
.ts-video { width: 100%; height: auto; display: block; }
.ts-stage.is-hit { outline: 3px solid #34D399; outline-offset: -3px; }

.ts-hit { position: absolute; left: 0; right: 0; bottom: 0; background: rgba(11,18,25,0.9); color: #fff;
  text-align: center; padding: 10px; font-family: 'IBM Plex Mono', ui-monospace, monospace;
  font-size: 15px; font-weight: 600; letter-spacing: 0.14em; }

.ts-engine { font-size: 11px; color: var(--ts-zinc); margin: 10px 0 0; }
.ts-engine p { margin: 0 0 4px; }
.ts-warn { color: var(--ts-red); font-weight: 500; max-width: 56ch; }

.ts-problem { border: 1px solid var(--ts-red); border-left-width: 3px; border-radius: 3px;
  padding: 14px; background: #FEFAF9; }
.ts-problem-title { font-size: 14px; font-weight: 600; color: var(--ts-red); margin: 0 0 6px; }
.ts-problem-body { font-size: 13px; color: #3C4C5C; margin: 0; max-width: 56ch; }
`;

/* ==========================================================================
   TESTING NOTES
   ==========================================================================

   STEP 1 — desktop, no setup, works right now
   -------------------------------------------
     npm run dev            # http://localhost:5173 is a secure context
   Open the Labels tab on your phone (or print a sheet), hold it up to your
   laptop webcam. This proves the encoder and the decode path in 30 seconds.

   STEP 2 — phone camera, needs HTTPS
   ----------------------------------
   A LAN IP over http will NOT get camera access. Use a tunnel:

     npx cloudflared tunnel --url http://localhost:5173
       ...or...
     npx ngrok http 5173

   Vite blocks unknown Host headers, so allow the tunnel domain:

     // vite.config.js
     export default {
       server: {
         host: true,
         allowedHosts: ['.trycloudflare.com', '.ngrok-free.app'],
       },
     }

   Open the https:// URL the tunnel prints on your phone. Camera works.

   STEP 3 — print real labels
   --------------------------
   Screen-to-screen scanning is misleadingly easy: backlit, high contrast,
   perfectly flat. Print a sheet on plain paper and test that instead. Then
   test the conditions that actually break scanning in the field:
     · matte label under fluorescent light with glare
     · curved surface (a phone case, a cable drum)
     · label at 30-40° rather than straight on
     · 20% of the code obscured by a thumb — EC level M tolerates ~15%
     · scuffed or partly peeled label

   The last one is why every tag prints the ID in human-readable mono. When
   the code fails, someone types six characters and moves on.

   STEP 4 — keep manual entry forever
   ----------------------------------
   Do not replace the text input with the camera. Put them side by side.
   Cameras fail on cracked lenses, in dark server rooms, with dead batteries,
   and on the one device whose label got soaked. Manual entry is the floor.

   INTEGRATION
   -----------
   In ScanView, add state and render this above the existing input:

     const [camera, setCamera] = useState(false);

     {camera && (
       <TagScanner
         onScan={(raw) => submit(raw)}   // submit() already runs parseScan()
         onClose={() => setCamera(false)}
       />
     )}
     <button className="ai-btn" onClick={() => setCamera((c) => !c)}>
       {camera ? 'Stop camera' : 'Use camera'}
     </button>

   onScan receives the raw decoded string, which for your tags is the full
   URL. The existing parseScan() already strips it down to AST-0107, so a
   phone's own camera app and this scanner both resolve identically.

   IOS NOTE
   --------
   iOS Safari has no BarcodeDetector, so it lazy-loads html5-qrcode (~300KB).
   The video element needs playsinline or iOS hijacks it into fullscreen —
   already set above. Safari also requires the getUserMedia call to originate
   from a user gesture on some versions, which is why this mounts behind a
   button rather than auto-starting.
   ========================================================================== */